import { COLORS, H4 } from 'assets/styled';
import { Field, Formik } from 'formik';
import { Dispatch, SetStateAction, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ApplyWrapper,
  ColumnContainer,
  Wrapper,
  Label,
  RowSpaceBetween,
  Title,
  TitleContainer,
  Container,
  FixedRatesSection,
  FixedRatesHeader,
  FixedRatesList,
  FixedRateItem,
  FixedRateKey,
  FixedRateValue,
  EmptyRatesText,
} from './EditBuyerSettingsModal.styled';
import Button from 'components/Button/Button';
import { editBuyerSettingsSchema } from './validation';
import { useBreakpointFlag } from 'utils/hooks/useBreakpointFlag';
import { FormikOuter } from '../EditCompanyDataModal/EditCompanyDataModal.styled';
import { useEditCompany } from '../hooks';
import { IEditCompanyDataDTO } from '../types';
import { Modal } from 'components/Modals/Modal/Modal';
import { useSelector } from 'react-redux';
import { IRootReducerState } from 'store/store';
import FormikSelect from 'components/Select/FormikSelect';
import { useGetCurrencyOptions } from '../helpers';
import { FixedExchangeRatesModal } from '../FixedExchangeRatesModal/FixedExchangeRatesModal';
import { PencilSimple } from '@phosphor-icons/react';
import Icon from 'components/Icon/Icon';
import { IFixedExchangeRates } from 'types/Company.types';

interface IEditBuyerSettingsModalProps {
  onCancel: () => void;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  data: any;
}
export const EditBuyerSettingsModal = ({
  isOpen,
  setIsOpen,
  onCancel,
  data,
}: IEditBuyerSettingsModalProps) => {
  const { t } = useTranslation();
  const { isTablet } = useBreakpointFlag();
  const currencyOptions = useGetCurrencyOptions();
  const [isFixedRatesModalOpen, setIsFixedRatesModalOpen] = useState(false);
  const [fixedExchangeRates, setFixedExchangeRates] = useState<
    IFixedExchangeRates | undefined
  >(data?.company_settings?.fixed_exchange_rates);

  const { company } = useSelector((state: IRootReducerState) => state.userInfo);

  const { mutate: editCompany, isLoading } = useEditCompany(Number(company.id));

  useEffect(() => {
    setFixedExchangeRates(data?.company_settings?.fixed_exchange_rates);
  }, [data?.company_settings?.fixed_exchange_rates]);

  const hasFixedRates =
    fixedExchangeRates && Object.keys(fixedExchangeRates).length > 0;

  const handleFixedRatesSave = (rates: IFixedExchangeRates) => {
    setFixedExchangeRates(rates);
  };

  const handleEditCompanySettings = (values: any) => {
    const editCompanyDataDTO: IEditCompanyDataDTO = {
      name: data.name,
      vat_number: data.vat_number,
      coc_number: data.coc_number,
      website: data.website,
      email: data.email,
      phone_number: data.phone_number,
      address: data.address,
      company_settings: {
        vat_percentage: data.company_settings?.vat_percentage,
        pricing_currency: data.company_settings?.pricing_currency,
        display_currency: values.display_currency,
        quotation_prefix: values.quotation_prefix,
        order_prefix: data.company_settings?.order_prefix,
        is_gross_visible: data.company_settings?.is_gross_visible,
        allowed_delivery_countries:
          data.company_settings?.allowed_delivery_countries,
        fixed_exchange_rates: fixedExchangeRates,
        additional_cart_details:
          data.company_settings?.additional_cart_details ?? {},
      },
    };
    editCompany(editCompanyDataDTO, {
      onSuccess: () => setIsOpen(false),
    });
  };

  const getInitialValues = () => {
    return {
      display_currency: data?.company_settings?.display_currency as string,
    };
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        setIsOpen(false);
      }}
      modalStyle={{
        position: 'fixed',
        margin: 'auto',
        width: isTablet ? '90%' : '700rem',
        minWidth: isTablet ? '90%' : 'auto',
      }}
    >
      <Container>
        <TitleContainer>
          <Title>{t('Buyer settings')}</Title>
        </TitleContainer>
        <FormikOuter>
          <Formik
            enableReinitialize
            onSubmit={() => {
              //
            }}
            initialValues={getInitialValues()}
            validationSchema={editBuyerSettingsSchema}
          >
            {({ values, setFieldValue, handleBlur, errors, isValid }) => {
              // const handleBlurFunction = (e: any, name: string) => {
              //   const value = e.target.value.trim();
              //   setFieldValue(name, value);
              //   handleBlur(e);
              // };
              return (
                <Wrapper>
                  <RowSpaceBetween>
                    <ColumnContainer>
                      <Label>
                        <H4>{t('Currency')}</H4>
                      </Label>
                      <Field
                        name="display_currency"
                        component={FormikSelect}
                        options={currencyOptions}
                        isMulti={false}
                        errorMessage={
                          errors['display_currency']
                            ? errors['display_currency']
                            : ''
                        }
                        disabled={false}
                        width="100%"
                      />
                    </ColumnContainer>
                  </RowSpaceBetween>
                  <FixedRatesSection>
                    <FixedRatesHeader>
                      <Label>
                        <H4>{t('Fixed Exchange Rates')}</H4>
                      </Label>
                      <Icon
                        svg={PencilSimple}
                        color={COLORS.GREEN}
                        size={20}
                        onClick={() => setIsFixedRatesModalOpen(true)}
                      />
                    </FixedRatesHeader>
                    {hasFixedRates ? (
                      <FixedRatesList>
                        {Object.entries(fixedExchangeRates).map(
                          ([key, value]) => (
                            <FixedRateItem key={key}>
                              <FixedRateKey>{key}</FixedRateKey>
                              <FixedRateValue>{String(value)}</FixedRateValue>
                            </FixedRateItem>
                          )
                        )}
                      </FixedRatesList>
                    ) : (
                      <EmptyRatesText>
                        {t('No fixed exchange rates configured')}
                      </EmptyRatesText>
                    )}
                  </FixedRatesSection>
                  <ApplyWrapper>
                    <Button
                      onClick={onCancel}
                      secondary
                      width="200rem"
                      label={t('Cancel')}
                    />
                    <Button
                      disabled={!isValid || isLoading}
                      onClick={() => {
                        if (isValid) {
                          handleEditCompanySettings(values);
                        }
                      }}
                      primary
                      width="200rem"
                      label={t('Apply')}
                    />
                  </ApplyWrapper>
                </Wrapper>
              );
            }}
          </Formik>
        </FormikOuter>
      </Container>
      {isFixedRatesModalOpen && (
        <FixedExchangeRatesModal
          isOpen={isFixedRatesModalOpen}
          setIsOpen={setIsFixedRatesModalOpen}
          onCancel={() => setIsFixedRatesModalOpen(false)}
          onSave={handleFixedRatesSave}
          fixedExchangeRates={fixedExchangeRates}
          companyCurrencyCode={data?.company_settings?.display_currency}
        />
      )}
    </Modal>
  );
};
